import { Injectable, computed, effect, inject, signal } from '@angular/core';

import { Subscription, interval } from 'rxjs';

import { BrijjSportsStateService } from '@core/state/brijjsports-state.service';

/**
 * Lightweight RxJS-backed countdown clock for the *active* basketball game.
 * Lives entirely inside the basketball plug-in. Uses a 1s `interval` and
 * writes ticks back to `BrijjSportsStateService.basketballClock` on the game.
 *
 * Only one game is "wired" at a time via `attach(sessionId)`; the live
 * scorer component owns the lifecycle.
 */
@Injectable({ providedIn: 'root' })
export class GameClockService {
  private readonly state = inject(BrijjSportsStateService);
  private readonly _attachedSessionId = signal<string | null>(null);
  private sub: Subscription | null = null;

  readonly attachedSessionId = this._attachedSessionId.asReadonly();

  /** Reactive view of the attached game's clock state. */
  readonly clock = computed(() => {
    const id = this._attachedSessionId();
    if (!id) return null;
    return this.state.basketballGameBySession(id)?.clock ?? null;
  });

  constructor() {
    // When a session is attached and the clock is running, drive a 1 Hz tick.
    effect(() => {
      const sessionId = this._attachedSessionId();
      const clock = this.clock();
      this.sub?.unsubscribe();
      this.sub = null;
      if (sessionId && clock?.running) {
        this.sub = interval(1000).subscribe(() => this.tick(sessionId));
      }
    });
  }

  attach(sessionId: string): void {
    this._attachedSessionId.set(sessionId);
  }

  detach(): void {
    this.sub?.unsubscribe();
    this.sub = null;
    this._attachedSessionId.set(null);
  }

  start(): void {
    const id = this._attachedSessionId();
    if (!id) return;
    this.state.patchBasketballClock(id, { running: true, lastTickAt: new Date().toISOString() });
  }

  pause(): void {
    const id = this._attachedSessionId();
    if (!id) return;
    this.state.patchBasketballClock(id, { running: false });
  }

  resetQuarter(): void {
    const id = this._attachedSessionId();
    const c = this.clock();
    if (!id || !c) return;
    this.state.patchBasketballClock(id, { remainingSec: c.quarterDurationSec, running: false });
  }

  /** Advance to the next quarter (or overtime period). Resets the timer. */
  advanceQuarter(): void {
    const id = this._attachedSessionId();
    const c = this.clock();
    if (!id || !c) return;
    this.state.patchBasketballClock(id, {
      currentQuarter: c.currentQuarter + 1,
      remainingSec: c.quarterDurationSec,
      running: false
    });
  }

  private tick(sessionId: string): void {
    const game = this.state.basketballGameBySession(sessionId);
    const c = game?.clock;
    if (!c || !c.running) return;
    const next = Math.max(0, c.remainingSec - 1);
    if (next === 0) {
      this.state.patchBasketballClock(sessionId, {
        remainingSec: 0,
        running: false,
        lastTickAt: new Date().toISOString()
      });
    } else {
      this.state.patchBasketballClock(sessionId, { remainingSec: next, lastTickAt: new Date().toISOString() });
    }
  }
}
